// Tiny, dependency-free i18n. Translates a curated v1 set of strings —
// landing heroes, paywall chips/CTAs, the language picker — and lets every
// other surface stay English (intentional v1 scope, per product decision).
//
// Reply-OUTPUT language is a different axis: see state.replyLanguage which
// is sent as options.language to /api/lab/generate. UI language only
// affects what the user reads in this SPA.
//
// Usage:
//   import { t, applyI18n, setLocale } from './i18n.js';
//   <h1 data-i18n="landing.default.title">…</h1>
//   applyI18n();              // paint everything
//   setLocale('es');          // switch and repaint

import { state } from './state.js';

export const SUPPORTED_LOCALES = ['en', 'es', 'fr', 'de', 'it', 'pt', 'nl'];

// Names shown in language pickers. Reused for both UI- and reply-language
// pickers so the user sees consistent labels.
export const LANG_LABELS = {
  en: '🇬🇧 English',
  es: '🇪🇸 Español',
  fr: '🇫🇷 Français',
  de: '🇩🇪 Deutsch',
  it: '🇮🇹 Italiano',
  pt: '🇵🇹 Português',
  nl: '🇳🇱 Nederlands',
};

// v1 translation surface — hero copy on each landing + paywall chips + CTAs.
// Anything not in this map falls back to the English value (or the key
// itself if even English is missing).
const messages = {
  en: {
    'landing.default.title':      'Your daily X reply pack. In your voice.',
    'landing.default.sub':        'TruthU drafts your daily X reply pack — tuned to your voice, in 7 languages. No subscription. No automation footprint.',
    'landing.suspension.title':   'Reply all day. Without getting flagged.',
    'landing.suspension.sub':     'TruthU drafts the replies. You paste them — your browser, your IP, your fingers. Zero automation footprint.',
    'landing.viral.title':        'Mine 200 viral tweets a week. Reply to the ones that matter.',
    'landing.viral.sub':          'Point TruthU at any List, handle, or topic. We surface what is heating up — and draft your angle before the algorithm cools.',
    'landing.variations.title':   '23 takes on every tweet. Pick the one that hits.',
    'landing.variations.sub':     'Spicy, contrarian, mock, educational, short-bold — every pack ships seven voices so you stop posting the first thing you think of.',
    'cta.start':                  'Get started →',
    'cta.start.suspension':       'Show me a safe reply now →',
    'cta.start.viral':            'Find what is trending in my niche →',
    'cta.start.variations':       'Generate 23 angles on one tweet →',
    'paywall.title':              'Top up. Then generate.',
    'paywall.intro':              'Pick an amount. Bigger top-ups unlock bonus credits. Min $5. No subscription. Credits never expire.',
    'paywall.chip.5':              '$5 · 5 cr',
    'paywall.chip.10':             '$10 · 12 cr · +20%',
    'paywall.chip.25':             '$25 · 35 cr · +40%',
    'paywall.chip.50':             '$50 · 75 cr · +50%',
    'paywall.recommended':         'Best value',
    'paywall.custom':              'Custom amount (min $5)',
    'paywall.refund':              'First pack underperforms your own average? We refund the credit. No paperwork, no subscription, credits never expire.',
    'paywall.cta':                 'Top up ${amount} → Stripe',
    'paywall.preview.title':       "Here's one we drafted for you, free.",
    'paywall.preview.hint':        'The other 22 unlock as soon as you top up.',
    'lang.reply':                  'Reply language',
    'lang.change':                 '(change)',
    'lang.ui':                     'Interface language',
    'auth.google':                 'Continue with Google',
    'auth.or':                     'or',
    'auth.continue':               'Create account →',
    'auth.signin':                 'Sign in →',
    'auth.toggle.to-signin':       'Already have an account? Sign in',
    'auth.toggle.to-signup':       "New here? Create an account",
    'checkout.processing.title':   'Processing payment…',
    'checkout.processing.sub':     'Your credits will appear in a few seconds.',
    'checkout.added':              '+${credits} credits added.',
    'checkout.canceled':           'Checkout canceled — no charge.',
    'checkout.timeout':            'Payment is still processing. Refresh in a minute.',
  },
  es: {
    'landing.default.title':      'Tu pack diario de respuestas en X. En tu voz.',
    'landing.default.sub':        'TruthU redacta tu pack diario de respuestas de X — en tu voz, en 7 idiomas. Sin suscripción. Sin huella de automatización.',
    'landing.suspension.title':   'Responde todo el día. Sin que te marquen.',
    'landing.suspension.sub':     'TruthU redacta las respuestas. Tú las pegas — tu navegador, tu IP, tus dedos. Cero huella de automatización.',
    'landing.viral.title':        'Mina 200 tweets virales a la semana. Responde a los que importan.',
    'landing.viral.sub':          'Apunta TruthU a cualquier Lista, cuenta o tema. Detectamos qué está calentando — y redactamos tu ángulo antes de que se enfríe.',
    'landing.variations.title':   '23 enfoques por cada tweet. Elige el que pegue.',
    'landing.variations.sub':     'Picante, contrario, irónico, educativo, breve y firme — cada pack lleva siete voces para que dejes de publicar lo primero que se te ocurre.',
    'cta.start':                  'Empezar →',
    'cta.start.suspension':       'Muéstrame una respuesta segura →',
    'cta.start.viral':            'Encuentra lo que es tendencia en mi nicho →',
    'cta.start.variations':       'Genera 23 ángulos sobre un tweet →',
    'paywall.title':              'Recarga. Luego genera.',
    'paywall.intro':              'Elige un importe. Recargas mayores desbloquean créditos extra. Mín. 5 $. Sin suscripción. Los créditos nunca caducan.',
    'paywall.chip.5':              '5 $ · 5 cr',
    'paywall.chip.10':             '10 $ · 12 cr · +20%',
    'paywall.chip.25':             '25 $ · 35 cr · +40%',
    'paywall.chip.50':             '50 $ · 75 cr · +50%',
    'paywall.recommended':         'Mejor valor',
    'paywall.custom':              'Importe personalizado (mín. 5 $)',
    'paywall.refund':              '¿El primer pack rinde menos que tu media? Te devolvemos el crédito. Sin papeleo, sin suscripción, los créditos nunca caducan.',
    'paywall.cta':                 'Recargar ${amount} → Stripe',
    'paywall.preview.title':       'Aquí tienes una que ya redactamos para ti, gratis.',
    'paywall.preview.hint':        'Las otras 22 se desbloquean en cuanto recargues.',
    'lang.reply':                  'Idioma de la respuesta',
    'lang.change':                 '(cambiar)',
    'lang.ui':                     'Idioma de la interfaz',
  },
  fr: {
    'landing.default.title':      'Ton pack quotidien de réponses X. Dans ta voix.',
    'landing.default.sub':        'TruthU rédige ton pack quotidien de réponses X — dans ta voix, en 7 langues. Sans abonnement. Aucune trace d\'automatisation.',
    'landing.suspension.title':   'Réponds toute la journée. Sans te faire signaler.',
    'landing.suspension.sub':     'TruthU rédige les réponses. Tu les colles — ton navigateur, ton IP, tes doigts. Zéro trace d\'automatisation.',
    'landing.viral.title':        'Explore 200 tweets viraux par semaine. Réponds à ceux qui comptent.',
    'landing.viral.sub':          'Pointe TruthU sur n\'importe quelle Liste, compte ou sujet. On détecte ce qui chauffe — et on rédige ton angle avant que ça refroidisse.',
    'landing.variations.title':   '23 angles par tweet. Choisis celui qui frappe.',
    'landing.variations.sub':     'Épicé, contraire, moqueur, éducatif, court et net — chaque pack embarque sept voix pour arrêter de poster la première idée venue.',
    'cta.start':                  'Commencer →',
    'cta.start.suspension':       'Montre-moi une réponse sûre →',
    'cta.start.viral':            'Trouve ce qui tend dans ma niche →',
    'cta.start.variations':       'Génère 23 angles sur un tweet →',
    'paywall.title':              'Recharge. Puis génère.',
    'paywall.intro':              'Choisis un montant. Les grosses recharges débloquent des crédits bonus. Min 5 $. Sans abonnement. Les crédits n\'expirent jamais.',
    'paywall.chip.5':              '5 $ · 5 cr',
    'paywall.chip.10':             '10 $ · 12 cr · +20 %',
    'paywall.chip.25':             '25 $ · 35 cr · +40 %',
    'paywall.chip.50':             '50 $ · 75 cr · +50 %',
    'paywall.recommended':         'Meilleur rapport',
    'paywall.custom':              'Montant personnalisé (min 5 $)',
    'paywall.refund':              'Ton premier pack est en-dessous de ta moyenne ? On rembourse le crédit. Sans paperasse, sans abonnement, crédits éternels.',
    'paywall.cta':                 'Recharger ${amount} → Stripe',
    'paywall.preview.title':       'En voici une qu\'on a déjà rédigée pour toi, gratuite.',
    'paywall.preview.hint':        'Les 22 autres se débloquent dès la recharge.',
    'lang.reply':                  'Langue de la réponse',
    'lang.change':                 '(changer)',
    'lang.ui':                     'Langue de l\'interface',
  },
  de: {
    'landing.default.title':      'Dein täglicher X-Antwort-Pack. In deiner Stimme.',
    'landing.default.sub':        'TruthU schreibt deinen täglichen X-Antwort-Pack — in deiner Stimme, in 7 Sprachen. Kein Abo. Keine Automatisierungsspur.',
    'landing.suspension.title':   'Antworte den ganzen Tag. Ohne gemeldet zu werden.',
    'landing.suspension.sub':     'TruthU entwirft die Antworten. Du fügst sie ein — dein Browser, deine IP, deine Finger. Null Automatisierungsspur.',
    'landing.viral.title':        '200 virale Tweets pro Woche durchforsten. Auf die antworten, die zählen.',
    'landing.viral.sub':          'Richte TruthU auf eine Liste, einen Account oder ein Thema. Wir zeigen, was gerade heiß ist — und entwerfen deinen Winkel, bevor es abkühlt.',
    'landing.variations.title':   '23 Angles pro Tweet. Wähle den, der trifft.',
    'landing.variations.sub':     'Scharf, konträr, spöttisch, lehrreich, knapp und entschieden — jeder Pack liefert sieben Stimmen, damit du nicht den ersten Einfall postest.',
    'cta.start':                  'Loslegen →',
    'cta.start.suspension':       'Zeig mir eine sichere Antwort →',
    'cta.start.viral':            'Finde, was in meiner Nische trendet →',
    'cta.start.variations':       'Generiere 23 Angles auf einen Tweet →',
    'paywall.title':              'Aufladen. Dann generieren.',
    'paywall.intro':              'Wähle einen Betrag. Größere Aufladungen schalten Bonus-Credits frei. Min. 5 $. Kein Abo. Credits verfallen nie.',
    'paywall.chip.5':              '5 $ · 5 Cr',
    'paywall.chip.10':             '10 $ · 12 Cr · +20%',
    'paywall.chip.25':             '25 $ · 35 Cr · +40%',
    'paywall.chip.50':             '50 $ · 75 Cr · +50%',
    'paywall.recommended':         'Beste Wahl',
    'paywall.custom':              'Eigener Betrag (min. 5 $)',
    'paywall.refund':              'Erster Pack unter deinem Durchschnitt? Wir erstatten den Credit. Kein Papierkram, kein Abo, Credits verfallen nie.',
    'paywall.cta':                 '${amount} aufladen → Stripe',
    'paywall.preview.title':       'Hier ist eine, die wir gratis für dich entworfen haben.',
    'paywall.preview.hint':        'Die anderen 22 schalten frei, sobald du aufgeladen hast.',
    'lang.reply':                  'Antwortsprache',
    'lang.change':                 '(ändern)',
    'lang.ui':                     'Oberflächensprache',
  },
  it: {
    'landing.default.title':      'Il tuo pack quotidiano di risposte X. Nella tua voce.',
    'landing.default.sub':        'TruthU scrive il tuo pack quotidiano di risposte X — nella tua voce, in 7 lingue. Niente abbonamenti. Zero impronta di automazione.',
    'landing.suspension.title':   'Rispondi tutto il giorno. Senza essere segnalato.',
    'landing.suspension.sub':     'TruthU scrive le risposte. Tu le incolli — il tuo browser, il tuo IP, le tue dita. Zero impronta di automazione.',
    'landing.viral.title':        'Estrai 200 tweet virali a settimana. Rispondi a quelli che contano.',
    'landing.viral.sub':          'Punta TruthU su qualsiasi Lista, account o tema. Mostriamo cosa sta scaldando — e scriviamo il tuo angolo prima che si raffreddi.',
    'landing.variations.title':   '23 angoli per ogni tweet. Scegli quello che colpisce.',
    'landing.variations.sub':     'Piccante, contrario, ironico, educativo, breve e netto — ogni pacchetto porta sette voci così smetti di postare la prima idea.',
    'cta.start':                  'Inizia →',
    'cta.start.suspension':       'Mostrami una risposta sicura →',
    'cta.start.viral':            'Trova cosa è di tendenza nella mia nicchia →',
    'cta.start.variations':       'Genera 23 angoli su un tweet →',
    'paywall.title':              'Ricarica. Poi genera.',
    'paywall.intro':              'Scegli un importo. Ricariche maggiori sbloccano crediti bonus. Min 5 $. Niente abbonamenti. I crediti non scadono mai.',
    'paywall.chip.5':              '5 $ · 5 cr',
    'paywall.chip.10':             '10 $ · 12 cr · +20%',
    'paywall.chip.25':             '25 $ · 35 cr · +40%',
    'paywall.chip.50':             '50 $ · 75 cr · +50%',
    'paywall.recommended':         'Miglior valore',
    'paywall.custom':              'Importo personalizzato (min 5 $)',
    'paywall.refund':              'Il primo pacchetto rende meno della tua media? Rimborsiamo il credito. Niente burocrazia, niente abbonamenti, crediti eterni.',
    'paywall.cta':                 'Ricarica ${amount} → Stripe',
    'paywall.preview.title':       'Eccone una che ti abbiamo già scritto, gratis.',
    'paywall.preview.hint':        'Le altre 22 si sbloccano appena ricarichi.',
    'lang.reply':                  'Lingua della risposta',
    'lang.change':                 '(cambia)',
    'lang.ui':                     'Lingua dell\'interfaccia',
  },
  pt: {
    'landing.default.title':      'O teu pack diário de respostas X. Na tua voz.',
    'landing.default.sub':        'A TruthU escreve o teu pack diário de respostas do X — na tua voz, em 7 idiomas. Sem assinatura. Zero pegada de automação.',
    'landing.suspension.title':   'Responde o dia todo. Sem ser sinalizado.',
    'landing.suspension.sub':     'A TruthU escreve as respostas. Tu colas — o teu browser, o teu IP, os teus dedos. Zero pegada de automação.',
    'landing.viral.title':        'Filtra 200 tweets virais por semana. Responde aos que importam.',
    'landing.viral.sub':          'Aponta a TruthU para qualquer Lista, conta ou tema. Mostramos o que está a aquecer — e escrevemos o teu ângulo antes que arrefeça.',
    'landing.variations.title':   '23 ângulos por tweet. Escolhe o que acerta.',
    'landing.variations.sub':     'Picante, contrário, irônico, educativo, curto e firme — cada pacote leva sete vozes para parares de postar a primeira ideia.',
    'cta.start':                  'Começar →',
    'cta.start.suspension':       'Mostra-me uma resposta segura →',
    'cta.start.viral':            'Descobre o que é tendência no meu nicho →',
    'cta.start.variations':       'Gera 23 ângulos num tweet →',
    'paywall.title':              'Recarrega. Depois gera.',
    'paywall.intro':              'Escolhe um valor. Recargas maiores desbloqueiam créditos bónus. Mín. 5 $. Sem assinatura. Créditos nunca expiram.',
    'paywall.chip.5':              '5 $ · 5 cr',
    'paywall.chip.10':             '10 $ · 12 cr · +20%',
    'paywall.chip.25':             '25 $ · 35 cr · +40%',
    'paywall.chip.50':             '50 $ · 75 cr · +50%',
    'paywall.recommended':         'Melhor valor',
    'paywall.custom':              'Valor personalizado (mín. 5 $)',
    'paywall.refund':              'Primeiro pacote rendeu menos que a tua média? Devolvemos o crédito. Sem papelada, sem assinatura, créditos eternos.',
    'paywall.cta':                 'Recarregar ${amount} → Stripe',
    'paywall.preview.title':       'Aqui está uma que já escrevemos para ti, grátis.',
    'paywall.preview.hint':        'As outras 22 desbloqueiam assim que recarregares.',
    'lang.reply':                  'Idioma da resposta',
    'lang.change':                 '(mudar)',
    'lang.ui':                     'Idioma da interface',
  },
  nl: {
    'landing.default.title':      'Jouw dagelijkse X-reactiepack. In jouw stem.',
    'landing.default.sub':        'TruthU schrijft jouw dagelijkse X-reactiepack — in jouw stem, in 7 talen. Geen abonnement. Geen automatisering-voetafdruk.',
    'landing.suspension.title':   'Reageer de hele dag. Zonder geflagd te worden.',
    'landing.suspension.sub':     'TruthU schrijft de reacties. Jij plakt ze — jouw browser, jouw IP, jouw vingers. Geen automatisering-voetafdruk.',
    'landing.viral.title':        'Doorzoek 200 virale tweets per week. Reageer op de tweets die ertoe doen.',
    'landing.viral.sub':          'Richt TruthU op elke Lijst, account of onderwerp. We tonen wat oploopt — en schrijven jouw invalshoek voordat het afkoelt.',
    'landing.variations.title':   '23 invalshoeken per tweet. Kies degene die raakt.',
    'landing.variations.sub':     'Pittig, tegendraads, spottend, leerzaam, kort en stevig — elk pack heeft zeven stemmen zodat je niet de eerste ingeving post.',
    'cta.start':                  'Beginnen →',
    'cta.start.suspension':       'Toon me een veilige reactie →',
    'cta.start.viral':            'Vind wat in mijn niche trending is →',
    'cta.start.variations':       'Genereer 23 invalshoeken op één tweet →',
    'paywall.title':              'Vul aan. Dan genereren.',
    'paywall.intro':              'Kies een bedrag. Grotere aanvullingen ontgrendelen bonuscredits. Min. $5. Geen abonnement. Credits verlopen nooit.',
    'paywall.chip.5':              '$5 · 5 cr',
    'paywall.chip.10':             '$10 · 12 cr · +20%',
    'paywall.chip.25':             '$25 · 35 cr · +40%',
    'paywall.chip.50':             '$50 · 75 cr · +50%',
    'paywall.recommended':         'Beste waarde',
    'paywall.custom':              'Eigen bedrag (min. $5)',
    'paywall.refund':              'Eerste pack onder je eigen gemiddelde? We betalen het credit terug. Geen papierwerk, geen abonnement, credits verlopen nooit.',
    'paywall.cta':                 'Vul ${amount} aan → Stripe',
    'paywall.preview.title':       'Hier is er één die we al gratis voor je schreven.',
    'paywall.preview.hint':        'De andere 22 worden ontgrendeld zodra je aanvult.',
    'lang.reply':                  'Reactietaal',
    'lang.change':                 '(wijzigen)',
    'lang.ui':                     'Interfacetaal',
  },
};

const interpolate = (str, vars) => {
  if (!vars) return str;
  return str.replace(/\$\{(\w+)\}/g, (_, k) => (vars[k] == null ? '' : String(vars[k])));
};

export const t = (key, vars, locale) => {
  const loc = locale || state.locale || 'en';
  const bag = messages[loc] || messages.en;
  const raw = bag[key] ?? messages.en[key] ?? key;
  return interpolate(raw, vars);
};

// Walks the DOM and paints every [data-i18n] element. `data-i18n-attr="alt"`
// targets a specific attribute instead of textContent. Vars come from
// `data-i18n-vars` parsed as JSON (rare; only used for {amount} on CTA).
export const applyI18n = (root = document) => {
  root.querySelectorAll('[data-i18n]').forEach(el => {
    const key  = el.getAttribute('data-i18n');
    const attr = el.getAttribute('data-i18n-attr');
    let vars;
    const v = el.getAttribute('data-i18n-vars');
    if (v) { try { vars = JSON.parse(v); } catch {} }
    const txt = t(key, vars);
    if (attr) el.setAttribute(attr, txt);
    else      el.textContent = txt;
  });
  document.documentElement.setAttribute('lang', state.locale || 'en');
};

export const setLocale = (loc) => {
  if (!SUPPORTED_LOCALES.includes(loc)) return;
  state.locale = loc;
  try { localStorage.setItem('hybrid.locale', loc); } catch {}
  applyI18n();
};
